import { useEffect, useState, useMemo } from 'react';
import JsonLd from '../../components/feature/JsonLd';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    document.title = 'Pricing Plans – AI BPO Call Center Outsourcing | Call Center Pros';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Transparent pricing for AI-powered BPO nearshore call center solutions. Choose from Starter, Professional, or Enterprise plans. No hidden fees. Free consultation available.');
  }, []);

  const faqSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How quickly can we get started?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our Starter plan can be set up within 48 hours, Professional within 24 hours, and Enterprise solutions are customized based on your needs. We handle recruitment, training, and system integration to ensure a smooth launch.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is included in the setup process?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Setup includes agent recruitment and screening, comprehensive training on your processes and systems, CRM and tool integration, quality assurance protocols, and a dedicated account manager to oversee the transition.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can we scale our team up or down?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely! One of our key advantages is flexibility. You can scale your team size up or down with just 2 weeks notice, allowing you to adapt to seasonal demands or business growth without long-term commitments.',
        },
      },
      {
        '@type': 'Question',
        name: 'What languages do your agents speak?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our nearshore locations provide native English and Spanish speakers. We can also accommodate other languages based on your specific requirements. All agents undergo language proficiency testing before assignment.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do you ensure quality?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We maintain quality through continuous monitoring, regular QA audits, performance scorecards, ongoing training programs, and customer satisfaction surveys. You receive detailed reports and have full visibility into agent performance.',
        },
      },
      {
        '@type': 'Question',
        name: 'What happens if we need to cancel?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We offer flexible contracts with 30-day notice periods for monthly plans. Annual plans receive discounted rates but require 60-day notice. We work with you to ensure a smooth transition if you decide to make changes.',
        },
      },
    ],
  }), []);

  const pricingPageSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Pricing Plans – AI BPO Call Center Outsourcing',
    url: `${siteUrl}/plans-and-pricing`,
    description: 'Transparent pricing for AI-powered BPO nearshore solutions. Starter, Professional, and Enterprise plans with no hidden fees.',
  }), [siteUrl]);

  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small businesses getting started with BPO',
      monthlyPrice: 2499,
      annualPrice: 24990,
      features: [
        '5 Dedicated Agents',
        '40 Hours/Week Coverage',
        'Email & Phone Support',
        'Basic CRM Integration',
        'Monthly Performance Reports',
        'Standard Training',
        '48-Hour Setup'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Professional',
      description: 'Ideal for growing businesses with higher volume',
      monthlyPrice: 4999,
      annualPrice: 49990,
      features: [
        '15 Dedicated Agents',
        '24/7 Coverage',
        'Multi-channel Support',
        'Advanced CRM Integration',
        'Weekly Performance Reports',
        'Custom Training Program',
        'Dedicated Account Manager',
        '24-Hour Setup',
        'Quality Assurance Team'
      ],
      cta: 'Get Started',
      popular: true
    },
    {
      name: 'Enterprise',
      description: 'Custom solutions for large-scale operations',
      monthlyPrice: null,
      annualPrice: null,
      features: [
        'Unlimited Agents',
        '24/7 Premium Coverage',
        'Omnichannel Support',
        'Full System Integration',
        'Real-time Analytics Dashboard',
        'Custom AI Solutions',
        'Dedicated Success Team',
        'Priority Setup',
        'Advanced Security & Compliance',
        'Custom SLAs'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const faqs = [
    {
      question: 'How quickly can we get started?',
      answer: 'Our Starter plan can be set up within 48 hours, Professional within 24 hours, and Enterprise solutions are customized based on your needs. We handle recruitment, training, and system integration to ensure a smooth launch.'
    },
    {
      question: 'What is included in the setup process?',
      answer: 'Setup includes agent recruitment and screening, comprehensive training on your processes and systems, CRM and tool integration, quality assurance protocols, and a dedicated account manager to oversee the transition.'
    },
    {
      question: 'Can we scale our team up or down?',
      answer: 'Absolutely! One of our key advantages is flexibility. You can scale your team size up or down with just 2 weeks notice, allowing you to adapt to seasonal demands or business growth without long-term commitments.'
    },
    {
      question: 'What languages do your agents speak?',
      answer: 'Our nearshore locations provide native English and Spanish speakers. We can also accommodate other languages based on your specific requirements. All agents undergo language proficiency testing before assignment.'
    },
    {
      question: 'How do you ensure quality?',
      answer: 'We maintain quality through continuous monitoring, regular QA audits, performance scorecards, ongoing training programs, and customer satisfaction surveys. You receive detailed reports and have full visibility into agent performance.'
    },
    {
      question: 'What happens if we need to cancel?',
      answer: 'We offer flexible contracts with 30-day notice periods for monthly plans. Annual plans receive discounted rates but require 60-day notice. We work with you to ensure a smooth transition if you decide to make changes.'
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="pricing-faq-schema" data={faqSchema} />
      <JsonLd id="pricing-page-schema" data={pricingPageSchema} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-700"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-6">
              Pricing Plans
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Simple, <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Transparent Pricing</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Choose the perfect plan for your business. All plans include our core features with no hidden fees.
            </p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-gray-200">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  billingCycle === 'monthly'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  billingCycle === 'annual'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual <span className="text-xs ml-1">(Save 17%)</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl border-2 transition-all duration-300 ${
                    plan.popular
                      ? 'border-blue-600 shadow-2xl scale-105'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-xl'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                    
                    <div className="mb-6">
                      {plan.monthlyPrice ? (
                        <>
                          <div className="flex items-baseline">
                            <span className="text-5xl font-bold text-gray-900">
                              ${billingCycle === 'monthly' ? plan.monthlyPrice.toLocaleString() : (plan.annualPrice / 12).toLocaleString()}
                            </span>
                            <span className="text-gray-600 ml-2">/month</span>
                          </div>
                          {billingCycle === 'annual' && (
                            <p className="text-sm text-gray-500 mt-2">
                              Billed annually at ${plan.annualPrice.toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="text-4xl font-bold text-gray-900">Custom</div>
                      )}
                    </div>

                    <a
                      href="/contact-us"
                      className={`block w-full text-center py-3 px-6 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer mb-8 ${
                        plan.popular
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      {plan.cta}
                    </a>

                    <div className="space-y-4">
                      <p className="text-sm font-semibold text-gray-900 mb-3">What's included:</p>
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <i className="ri-check-line text-blue-600 text-sm"></i>
                          </div>
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Compare Plans</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See which features are included in each plan
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Starter</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Professional</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Dedicated Agents</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">5</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">15</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">Unlimited</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Coverage Hours</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">40 hrs/week</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">24/7</td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">24/7 Premium</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">CRM Integration</td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Account Manager</td>
                      <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">AI Solutions</td>
                      <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Custom SLAs</td>
                      <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300"></i></td>
                      <td className="px-6 py-4 text-center"><i className="ri-check-line text-blue-600"></i></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-4">
                FAQ
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600">
                Everything you need to know about our pricing and services
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-xl text-gray-600 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-4">Still Have Questions?</h2>
          <p className="text-blue-100 mb-10 max-w-2xl mx-auto text-lg">
            Our team is here to help you find the perfect solution for your business
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact-us" 
              className="inline-block bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Contact Sales Team
            </a>
            <a 
              href="/services" 
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Explore Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;